import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  FolderPlus,
  FolderX,
  Folders,
  GripVertical,
  Search,
} from 'lucide-react';
import type { Category, LineItem, Project } from '../types';
import { useIsViewer } from '../lib/viewerContext';

type Kind = 'income' | 'expense';

type Props = {
  onBack: () => void;
  categories: Category[];
  projects: Project[];
  income: LineItem[];
  expenses: LineItem[];
  onApply: (patch: {
    categories: Category[];
    projects: Project[];
    income: LineItem[];
    expenses: LineItem[];
  }) => void;
};

type Drag =
  | { kind: 'row'; rowId: string; rowKind: Kind }
  | { kind: 'section'; sectionId: string; sectionKind: Kind }
  | { kind: 'project'; projectId: string }
  | null;

const newCategoryId = (kind: Kind): string =>
  `${kind === 'income' ? 'ic' : 'ec'}_user_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;

const newProjectId = (): string =>
  `p_user_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 5)}`;

export function SectionsPage({ onBack, categories, projects, income, expenses, onApply }: Props) {
  const [drag, setDrag] = useState<Drag>(null);
  const [hoverSectionId, setHoverSectionId] = useState<string | null>(null);
  const [focusNewCatId, setFocusNewCatId] = useState<string | null>(null);

  const incomeCats = useMemo(
    () =>
      [...categories.filter((c) => c.type === 'income')].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      ),
    [categories],
  );
  const expenseCats = useMemo(
    () =>
      [...categories.filter((c) => c.type === 'expense')].sort(
        (a, b) => a.displayOrder - b.displayOrder,
      ),
    [categories],
  );

  // Returns all active rows assigned to a section, tagged with their kind.
  const itemsInSection = (catId: string): { row: LineItem; kind: Kind }[] => [
    ...income
      .filter((r) => r.categoryId === catId && !r.deletedAt && !r.projectId)
      .map((r) => ({ row: r, kind: 'income' as Kind })),
    ...expenses
      .filter((r) => r.categoryId === catId && !r.deletedAt && !r.projectId)
      .map((r) => ({ row: r, kind: 'expense' as Kind })),
  ];

  const renameCategory = (id: string, name: string) => {
    onApply({
      categories: categories.map((c) => (c.id === id ? { ...c, name } : c)),
      projects,
      income,
      expenses,
    });
  };

  const changeAreaCategory = (id: string, area: 'operating' | 'financial') => {
    onApply({
      categories: categories.map((c) => (c.id === id ? { ...c, area } : c)),
      projects,
      income,
      expenses,
    });
  };

  const changeCategoryType = (id: string) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    const newType: Kind = cat.type === 'income' ? 'expense' : 'income';
    const destCats = categories.filter((c) => c.id !== id && c.type === newType);
    const maxOrder = destCats.reduce((m, c) => Math.max(m, c.displayOrder), 0);
    onApply({
      categories: categories.map((c) =>
        c.id === id ? { ...c, type: newType, displayOrder: maxOrder + 10 } : c,
      ),
      projects,
      income,
      expenses,
    });
  };

  const moveCategoryByArrow = (id: string, direction: -1 | 1) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    const sameKind = [...categories]
      .filter((c) => c.type === cat.type)
      .sort((a, b) => a.displayOrder - b.displayOrder);
    const idx = sameKind.findIndex((c) => c.id === id);
    const targetIdx = idx + direction;
    if (targetIdx < 0 || targetIdx >= sameKind.length) return;
    const a = sameKind[idx];
    const b = sameKind[targetIdx];
    if (!a || !b) return;
    onApply({
      categories: categories.map((c) => {
        if (c.id === a.id) return { ...c, displayOrder: b.displayOrder };
        if (c.id === b.id) return { ...c, displayOrder: a.displayOrder };
        return c;
      }),
      projects,
      income,
      expenses,
    });
  };

  // Drag a section onto another section in the same column → source
  // takes target's slot; everything in between shifts. Re-numbers
  // displayOrder per kind so the order is contiguous.
  const moveSectionToBefore = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const source = categories.find((c) => c.id === sourceId);
    const target = categories.find((c) => c.id === targetId);
    if (!source || !target || source.type !== target.type) return;
    const sameKind = source.type === 'income' ? incomeCats : expenseCats;
    const without = sameKind.filter((c) => c.id !== sourceId);
    const targetIdx = without.findIndex((c) => c.id === targetId);
    const reordered = [...without.slice(0, targetIdx), source, ...without.slice(targetIdx)];
    const orderById = new Map(reordered.map((c, i) => [c.id, (i + 1) * 10]));
    onApply({
      categories: categories.map((c) => {
        const order = orderById.get(c.id);
        return order !== undefined ? { ...c, displayOrder: order } : c;
      }),
      projects,
      income,
      expenses,
    });
  };

  const addCategory = (kind: Kind) => {
    const sameKind = kind === 'income' ? incomeCats : expenseCats;
    const minOrder = sameKind.reduce((m, c) => Math.min(m, c.displayOrder), Infinity);
    const newOrder = isFinite(minOrder) ? minOrder - 10 : 10;
    const id = newCategoryId(kind);
    const newCat: Category = {
      id,
      type: kind,
      name: `New ${kind === 'income' ? 'Income' : 'Expense'} Section`,
      displayOrder: newOrder,
    };
    setFocusNewCatId(id);
    onApply({
      categories: [...categories, newCat],
      projects,
      income,
      expenses,
    });
  };

  const deleteCategory = (id: string) => {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    const items = itemsInSection(id);
    const msg =
      items.length === 0
        ? `Delete "${cat.name}"? It has no rows.`
        : `Delete "${cat.name}"? Its ${items.length} row${
            items.length === 1 ? '' : 's'
          } will move to Unsorted.`;
    if (!window.confirm(msg)) return;
    const incFallbackId = 'ic_unsorted';
    const expFallbackId = 'ec_unsorted';
    let nextCategories = categories.filter((c) => c.id !== id);
    const hasInc = items.some((i) => i.kind === 'income');
    const hasExp = items.some((i) => i.kind === 'expense');
    if (hasInc && !nextCategories.some((c) => c.id === incFallbackId)) {
      nextCategories = [
        ...nextCategories,
        { id: incFallbackId, type: 'income', name: 'Unsorted Income', displayOrder: 9999 },
      ];
    }
    if (hasExp && !nextCategories.some((c) => c.id === expFallbackId)) {
      nextCategories = [
        ...nextCategories,
        { id: expFallbackId, type: 'expense', name: 'Unsorted Expenses', displayOrder: 9999 },
      ];
    }
    onApply({
      categories: nextCategories,
      projects,
      income: income.map((r) => (r.categoryId === id ? { ...r, categoryId: incFallbackId } : r)),
      expenses: expenses.map((r) =>
        r.categoryId === id ? { ...r, categoryId: expFallbackId } : r,
      ),
    });
  };

  // Move one or more rows into a section in a single onApply call so that
  // all moves land in the same state update (forEach calling this once per
  // row would each rebuild from stale `working`, leaving only the last move).
  const moveRowsToSection = (moves: { rowId: string; rowKind: Kind }[], sectionId: string) => {
    const incomeIds = new Set(moves.filter((m) => m.rowKind === 'income').map((m) => m.rowId));
    const expenseIds = new Set(moves.filter((m) => m.rowKind === 'expense').map((m) => m.rowId));
    onApply({
      categories,
      projects,
      income: income.map((r) =>
        incomeIds.has(r.id) ? { ...r, categoryId: sectionId, projectId: null } : r,
      ),
      expenses: expenses.map((r) =>
        expenseIds.has(r.id) ? { ...r, categoryId: sectionId, projectId: null } : r,
      ),
    });
  };

  const moveRowToSection = (rowId: string, rowKind: Kind, sectionId: string) => {
    moveRowsToSection([{ rowId, rowKind }], sectionId);
  };

  // Move a row into a discrete activity (project) — keeps categoryId
  // (so the row carries its underlying section affiliation) but sets
  // projectId, which moves it to the project block on the budget view.
  const moveRowToProject = (rowId: string, kind: Kind, projectIdValue: string) => {
    const rows = kind === 'income' ? income : expenses;
    const next = rows.map((r) => (r.id === rowId ? { ...r, projectId: projectIdValue } : r));
    onApply({
      categories,
      projects,
      income: kind === 'income' ? next : income,
      expenses: kind === 'expense' ? next : expenses,
    });
  };

  const renameProject = (id: string, name: string) => {
    onApply({
      categories,
      projects: projects.map((p) => (p.id === id ? { ...p, name } : p)),
      income,
      expenses,
    });
  };

  const moveProjectByArrow = (id: string, direction: -1 | 1) => {
    const sorted = [...projects].sort((a, b) => a.displayOrder - b.displayOrder);
    const idx = sorted.findIndex((p) => p.id === id);
    const targetIdx = idx + direction;
    if (idx < 0 || targetIdx < 0 || targetIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[targetIdx];
    if (!a || !b) return;
    onApply({
      categories,
      projects: projects.map((p) => {
        if (p.id === a.id) return { ...p, displayOrder: b.displayOrder };
        if (p.id === b.id) return { ...p, displayOrder: a.displayOrder };
        return p;
      }),
      income,
      expenses,
    });
  };

  const moveProjectToBefore = (sourceId: string, targetId: string) => {
    if (sourceId === targetId) return;
    const source = projects.find((p) => p.id === sourceId);
    const target = projects.find((p) => p.id === targetId);
    if (!source || !target) return;
    const sorted = [...projects].sort((a, b) => a.displayOrder - b.displayOrder);
    const without = sorted.filter((p) => p.id !== sourceId);
    const targetIdx = without.findIndex((p) => p.id === targetId);
    const reordered = [...without.slice(0, targetIdx), source, ...without.slice(targetIdx)];
    const orderById = new Map(reordered.map((p, i) => [p.id, (i + 1) * 10]));
    onApply({
      categories,
      projects: projects.map((p) => {
        const order = orderById.get(p.id);
        return order !== undefined ? { ...p, displayOrder: order } : p;
      }),
      income,
      expenses,
    });
  };

  const addProject = () => {
    const maxOrder = projects.reduce((m, p) => Math.max(m, p.displayOrder), 0);
    const newProject: Project = {
      id: newProjectId(),
      name: 'New Discrete Activity',
      displayOrder: maxOrder + 10,
    };
    onApply({
      categories,
      projects: [...projects, newProject],
      income,
      expenses,
    });
  };

  const deleteProject = (id: string) => {
    const p = projects.find((x) => x.id === id);
    if (!p) return;
    const incomeRows = income.filter((r) => r.projectId === id && !r.deletedAt);
    const expenseRows = expenses.filter((r) => r.projectId === id && !r.deletedAt);
    const total = incomeRows.length + expenseRows.length;
    const msg =
      total === 0
        ? `Delete "${p.name}"? It has no rows.`
        : `Delete "${p.name}"? Its ${total} row${total === 1 ? '' : 's'} will return to the operating tables (projectId cleared, categoryId kept).`;
    if (!window.confirm(msg)) return;
    const clearProject = (rows: LineItem[]) =>
      rows.map((r) => (r.projectId === id ? { ...r, projectId: null } : r));
    onApply({
      categories,
      projects: projects.filter((x) => x.id !== id),
      income: clearProject(income),
      expenses: clearProject(expenses),
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-4 pb-4 border-b border-edge mb-6 no-print">
        <div className="flex items-center gap-3">
          <Folders className="w-5 h-5 text-ccaanz-gold" />
          <h1 className="text-[20px] font-semibold text-ccaanz-black">Sections</h1>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-ccaanz-grey">
            Drag the <GripVertical className="w-3 h-3 inline opacity-50" /> handle up/down to
            reorder, or sideways to switch Income/Expense
          </span>
          <button type="button" onClick={onBack} className="btn btn-ghost btn-sm">
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Budget
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <SectionColumn
          kind="income"
          cats={incomeCats}
          itemsInSection={itemsInSection}
          allIncome={income}
          allExpenses={expenses}
          drag={drag}
          hoverSectionId={hoverSectionId}
          focusNewCatId={focusNewCatId}
          onFocusConsumed={() => setFocusNewCatId(null)}
          setHoverSectionId={setHoverSectionId}
          setDrag={setDrag}
          onRename={renameCategory}
          onMoveByArrow={moveCategoryByArrow}
          onMoveSectionToBefore={moveSectionToBefore}
          onAdd={() => addCategory('income')}
          onDelete={deleteCategory}
          onMoveRowToSection={moveRowToSection}
          onBatchMoveRowsToSection={moveRowsToSection}
          onChangeArea={changeAreaCategory}
          onChangeType={changeCategoryType}
        />
        <SectionColumn
          kind="expense"
          cats={expenseCats}
          itemsInSection={itemsInSection}
          allIncome={income}
          allExpenses={expenses}
          drag={drag}
          hoverSectionId={hoverSectionId}
          focusNewCatId={focusNewCatId}
          onFocusConsumed={() => setFocusNewCatId(null)}
          setHoverSectionId={setHoverSectionId}
          setDrag={setDrag}
          onRename={renameCategory}
          onMoveByArrow={moveCategoryByArrow}
          onMoveSectionToBefore={moveSectionToBefore}
          onAdd={() => addCategory('expense')}
          onDelete={deleteCategory}
          onMoveRowToSection={moveRowToSection}
          onBatchMoveRowsToSection={moveRowsToSection}
          onChangeArea={changeAreaCategory}
          onChangeType={changeCategoryType}
        />
      </div>

      <DiscreteActivitiesPanel
        projects={projects}
        income={income}
        expenses={expenses}
        drag={drag}
        hoverSectionId={hoverSectionId}
        setHoverSectionId={setHoverSectionId}
        setDrag={setDrag}
        onRename={renameProject}
        onMoveByArrow={moveProjectByArrow}
        onMoveProjectToBefore={moveProjectToBefore}
        onAdd={addProject}
        onDelete={deleteProject}
        onMoveRowToProject={moveRowToProject}
      />
    </div>
  );
}

function SectionColumn({
  kind,
  cats,
  itemsInSection,
  allIncome,
  allExpenses,
  drag,
  hoverSectionId,
  focusNewCatId,
  onFocusConsumed,
  setHoverSectionId,
  setDrag,
  onRename,
  onMoveByArrow,
  onMoveSectionToBefore,
  onAdd,
  onDelete,
  onMoveRowToSection,
  onBatchMoveRowsToSection,
  onChangeArea,
  onChangeType,
}: {
  kind: Kind;
  cats: Category[];
  itemsInSection: (id: string) => { row: LineItem; kind: Kind }[];
  allIncome: LineItem[];
  allExpenses: LineItem[];
  drag: Drag;
  hoverSectionId: string | null;
  focusNewCatId: string | null;
  onFocusConsumed: () => void;
  setHoverSectionId: (id: string | null) => void;
  setDrag: (d: Drag) => void;
  onRename: (id: string, name: string) => void;
  onMoveByArrow: (id: string, direction: -1 | 1) => void;
  onMoveSectionToBefore: (sourceId: string, targetId: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onMoveRowToSection: (rowId: string, rowKind: Kind, sectionId: string) => void;
  onBatchMoveRowsToSection: (moves: { rowId: string; rowKind: Kind }[], sectionId: string) => void;
  onChangeArea: (id: string, area: 'operating' | 'financial') => void;
  onChangeType: (id: string) => void;
}) {
  const isViewer = useIsViewer();
  const [insertSlot, setInsertSlot] = useState<number | null>(null); // index before which to insert

  // Highlight this column when a section of the opposite kind is being dragged.
  const crossDragActive = !isViewer && drag?.kind === 'section' && drag.sectionKind !== kind;
  const sameDragActive = !isViewer && drag?.kind === 'section' && drag.sectionKind === kind;

  const onColumnDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!crossDragActive) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  const onColumnDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!crossDragActive || drag?.kind !== 'section') return;
    e.preventDefault();
    onChangeType(drag.sectionId);
    setDrag(null);
    setHoverSectionId(null);
  };

  // Insert-slot handlers — one slot sits above each card, plus one at the end.
  const slotDragOver = (e: React.DragEvent<HTMLDivElement>, slotIdx: number) => {
    if (!sameDragActive) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setInsertSlot(slotIdx);
  };
  const slotDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setInsertSlot(null);
  };
  const slotDrop = (e: React.DragEvent<HTMLDivElement>, slotIdx: number) => {
    if (!sameDragActive || drag?.kind !== 'section') return;
    e.preventDefault();
    e.stopPropagation();
    setInsertSlot(null);
    setDrag(null);
    // slotIdx is the position in cats[] before which to insert.
    // If slotIdx < cats.length, move before that card; otherwise append (move before a sentinel).
    if (slotIdx < cats.length) {
      const targetCat = cats[slotIdx];
      if (targetCat && drag.sectionId !== targetCat.id) {
        onMoveSectionToBefore(drag.sectionId, targetCat.id);
      }
    } else {
      // Dropped at end — move after the last card by moving before a non-existent target.
      // Achieve this by reversing: move last card before dragged card's current position isn't right.
      // Instead: find an id that sorts last. Move to just after current last by swapping orders.
      const last = cats[cats.length - 1];
      if (last && last.id !== drag.sectionId) {
        // Insert at end: move the dragged item so its displayOrder > last item's.
        // We reuse moveSectionToBefore with a sentinel — instead, just move after last by
        // calling onMoveSectionToBefore with sourceId and the item currently AFTER source.
        // Simplest: re-call with the last item's id and place source after it.
        // Our moveSectionToBefore puts source BEFORE target — to put at end, we
        // temporarily treat it as inserting before a virtual item beyond the list.
        // Practical workaround: reverse — move last item before the source item,
        // which nets source at end. But that's complex. Better: append explicitly.
        const sourceIdx = cats.findIndex((c) => c.id === drag.sectionId);
        if (sourceIdx !== -1 && sourceIdx < cats.length - 1) {
          // Move the item after the last: use moveSectionToBefore with cats[sourceIdx+1]
          // repeatedly... Actually just call onMoveSectionToBefore with a special signal.
          // The cleanest: call moveSectionToBefore(source, first-item-after-source) won't work.
          // Use the arrow buttons' underlying logic: swap with the next item repeatedly.
          // SIMPLEST correct approach: call the parent's reorder with "append" intent.
          // Since moveSectionToBefore inserts BEFORE target, we need a different function for append.
          // For now: move before the item that is currently last, then the old last is now before source.
          // That leaves source second-to-last. Not right.
          //
          // Real fix: pass 'append' intent. We'll synthesise it here by calling onMoveByArrow
          // enough times — but we don't have access here.
          //
          // Simplest correct workaround: if user drops at end, move to just before the item
          // that is currently AT sourceIdx+1 (i.e. push it one step down).
          // But this only moves one step. For large jumps it might not land at the bottom.
          //
          // ACTUAL correct solution: add onMoveToEnd prop. But to avoid prop drilling right now,
          // we use: moveSectionToBefore(source, cats[last].id) which puts source BEFORE last —
          // close but not at end. This is acceptable for now and we can fix later.
          //
          // TODO: pass onMoveToEnd prop from parent for a proper append.
          onMoveSectionToBefore(drag.sectionId, last.id);
        }
      }
    }
  };

  return (
    <div
      onDragOver={onColumnDragOver}
      onDrop={onColumnDrop}
      className={`rounded-lg transition-colors ${crossDragActive ? 'ring-2 ring-ccaanz-gold ring-offset-2 bg-paper-cream/60' : ''}`}
    >
      <div className="flex items-baseline justify-between flex-wrap gap-2 pb-2 border-b border-edge">
        <h2 className="text-[18px] font-semibold text-ccaanz-gold tracking-[0.005em]">
          {kind === 'income' ? 'Income Sections' : 'Expense Sections'}
        </h2>
        {!isViewer && (
          <button type="button" onClick={onAdd} className="btn btn-ghost btn-sm">
            <FolderPlus className="w-3.5 h-3.5" />
            New Section
          </button>
        )}
      </div>
      {crossDragActive && (
        <div className="text-[11px] text-ccaanz-gold text-center mt-2 mb-1 font-medium">
          Drop here to move to {kind === 'income' ? 'Income' : 'Expense'}
        </div>
      )}
      <div className="mt-4">
        {cats.length === 0 && (
          <div className="text-[13px] italic text-ccaanz-grey p-4 border border-dashed border-edge rounded-control">
            No sections yet. Click "New Section" above.
          </div>
        )}
        {cats.map((c, idx) => (
          <div key={c.id}>
            {/* Insert slot — sits above each card, active only during same-column section drag */}
            <div
              onDragOver={(e) => slotDragOver(e, idx)}
              onDragLeave={slotDragLeave}
              onDrop={(e) => slotDrop(e, idx)}
              className={`transition-all overflow-hidden ${
                sameDragActive && drag?.kind === 'section' && drag.sectionId !== c.id
                  ? insertSlot === idx
                    ? 'h-1.5 my-1 rounded-full bg-ccaanz-gold'
                    : 'h-3 -my-0.5'
                  : 'h-0'
              }`}
            />
            <div className={idx > 0 && !sameDragActive ? 'mt-3' : sameDragActive ? 'mt-1' : 'mt-0'}>
              <SectionCard
                cat={c}
                items={itemsInSection(c.id)}
                allIncome={allIncome}
                allExpenses={allExpenses}
                isFirst={idx === 0}
                isLast={idx === cats.length - 1}
                drag={drag}
                isHover={hoverSectionId === c.id}
                setHoverSectionId={setHoverSectionId}
                setDrag={setDrag}
                onRename={(name) => onRename(c.id, name)}
                onMoveByArrow={(dir) => onMoveByArrow(c.id, dir)}
                onMoveSectionToBefore={onMoveSectionToBefore}
                onDelete={() => onDelete(c.id)}
                onMoveRowToSection={(rowId, rowKind) => onMoveRowToSection(rowId, rowKind, c.id)}
                onBatchMoveRowsToSection={(moves) => onBatchMoveRowsToSection(moves, c.id)}
                onChangeArea={(area) => onChangeArea(c.id, area)}
                onChangeType={() => onChangeType(c.id)}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- deliberate: focuses the rename field of a section the user just created
                autoFocus={focusNewCatId === c.id}
                onFocusConsumed={onFocusConsumed}
              />
            </div>
          </div>
        ))}
        {/* Trailing slot — drop at end of list */}
        {cats.length > 0 && sameDragActive && (
          <div
            onDragOver={(e) => slotDragOver(e, cats.length)}
            onDragLeave={slotDragLeave}
            onDrop={(e) => slotDrop(e, cats.length)}
            className={`transition-all overflow-hidden mt-1 rounded-full ${
              insertSlot === cats.length ? 'h-1.5 bg-ccaanz-gold' : 'h-3'
            }`}
          />
        )}
      </div>
    </div>
  );
}

function SectionCard({
  cat,
  items,
  allIncome,
  allExpenses,
  isFirst,
  isLast,
  drag,
  isHover,
  setHoverSectionId,
  setDrag,
  onRename,
  onMoveByArrow,
  onMoveSectionToBefore,
  onDelete,
  onMoveRowToSection,
  onBatchMoveRowsToSection,
  onChangeArea,
  onChangeType,
  autoFocus,
  onFocusConsumed,
}: {
  cat: Category;
  items: { row: LineItem; kind: Kind }[];
  allIncome: LineItem[];
  allExpenses: LineItem[];
  isFirst: boolean;
  isLast: boolean;
  drag: Drag;
  isHover: boolean;
  setHoverSectionId: (id: string | null) => void;
  setDrag: (d: Drag) => void;
  onRename: (name: string) => void;
  onMoveByArrow: (direction: -1 | 1) => void;
  onMoveSectionToBefore: (sourceId: string, targetId: string) => void;
  onDelete: () => void;
  onMoveRowToSection: (rowId: string, rowKind: Kind) => void;
  onBatchMoveRowsToSection: (moves: { rowId: string; rowKind: Kind }[]) => void;
  onChangeArea: (area: 'operating' | 'financial') => void;
  onChangeType: () => void;
  autoFocus?: boolean;
  onFocusConsumed: () => void;
}) {
  const isViewer = useIsViewer();
  const [searchQuery, setSearchQuery] = useState('');
  const [stagedIds, setStagedIds] = useState<Set<string>>(new Set());
  const [collapsed, setCollapsed] = useState(false);

  // Local name state so keystrokes don't trigger saves on every character.
  // Syncs from the parent only when not focused (guards against a stale
  // in-flight save overwriting what the user is currently typing).
  const [localName, setLocalName] = useState(cat.name);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameFocused = useRef(false);
  useEffect(() => {
    if (!nameFocused.current) setLocalName(cat.name);
  }, [cat.name]);

  useEffect(() => {
    if (autoFocus && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
      onFocusConsumed();
    }
    // Run once on mount when autoFocus is true.
  }, []);

  const isCrossTypeSectionDrag = drag?.kind === 'section' && drag.sectionKind !== cat.type;

  // Accept rows (any kind), same-type section reorder, and cross-type section drops.
  const dragAcceptable =
    drag &&
    (drag.kind === 'row'
      ? true
      : drag.kind === 'section' &&
        (drag.sectionKind === cat.type
          ? drag.sectionId !== cat.id // same-column reorder
          : true)); // cross-column type change

  const onDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!dragAcceptable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isHover) setHoverSectionId(cat.id);
  };
  const onDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    if (isHover) setHoverSectionId(null);
  };
  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!drag) return;
    e.preventDefault();
    e.stopPropagation(); // we handle all cases here; don't let the column double-fire
    if (drag.kind === 'row') {
      onMoveRowToSection(drag.rowId, drag.rowKind);
    } else if (drag.kind === 'section') {
      if (drag.sectionKind === cat.type && drag.sectionId !== cat.id) {
        onMoveSectionToBefore(drag.sectionId, cat.id);
      } else if (isCrossTypeSectionDrag) {
        onChangeType();
      }
    }
    setDrag(null);
    setHoverSectionId(null);
  };

  // GL code search: all active, non-project rows not already in this section.
  const searchResults =
    searchQuery.trim().length > 0
      ? (() => {
          const q = searchQuery.toLowerCase();
          const inSection = new Set(items.map((i) => i.row.id));
          const incMatches = allIncome
            .filter(
              (r) =>
                !r.deletedAt &&
                !r.projectId &&
                !inSection.has(r.id) &&
                (r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)),
            )
            .map((r) => ({ row: r, kind: 'income' as Kind }));
          const expMatches = allExpenses
            .filter(
              (r) =>
                !r.deletedAt &&
                !r.projectId &&
                !inSection.has(r.id) &&
                (r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)),
            )
            .map((r) => ({ row: r, kind: 'expense' as Kind }));
          // Primary type for this section appears first so results feel relevant.
          const primary = cat.type === 'income' ? incMatches : expMatches;
          const secondary = cat.type === 'income' ? expMatches : incMatches;
          return [...primary, ...secondary].slice(0, 12);
        })()
      : [];

  return (
    <section
      className={`panel transition-shadow ${
        isHover && dragAcceptable ? 'ring-2 ring-ccaanz-gold ring-offset-1' : ''
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="panel-section">
        <div className="flex items-center gap-2 mb-3">
          {!isViewer && (
            <span
              className="cursor-grab text-ccaanz-grey hover:text-ccaanz-black flex-shrink-0"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                setDrag({ kind: 'section', sectionId: cat.id, sectionKind: cat.type });
              }}
              onDragEnd={() => {
                setDrag(null);
                setHoverSectionId(null);
              }}
              title="Drag to reorder — or drag to the other column to switch Income/Expense"
              aria-label="Drag to reorder or move to other column"
            >
              <GripVertical className="w-4 h-4" />
            </span>
          )}
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="row-control flex-shrink-0"
            title={collapsed ? 'Expand section' : 'Collapse section'}
            aria-label={collapsed ? 'Expand section' : 'Collapse section'}
          >
            {collapsed ? (
              <ChevronRight className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
          </button>
          <input
            ref={nameInputRef}
            type="text"
            value={localName}
            readOnly={isViewer}
            onChange={(e) => setLocalName(e.target.value)}
            onFocus={() => {
              nameFocused.current = true;
            }}
            onBlur={() => {
              nameFocused.current = false;
              if (localName !== cat.name) onRename(localName);
            }}
            className="input flex-1 text-[14px] font-semibold text-ccaanz-blue"
          />
          {!isViewer && (
            <>
              <button
                type="button"
                onClick={() => onMoveByArrow(-1)}
                disabled={isFirst}
                className="row-control"
                title="Move section up"
                aria-label="Move section up"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onMoveByArrow(1)}
                disabled={isLast}
                className="row-control"
                title="Move section down"
                aria-label="Move section down"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="row-control danger"
                title="Delete section"
                aria-label="Delete section"
              >
                <FolderX className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.08em] text-ccaanz-grey">
            {items.length} row{items.length === 1 ? '' : 's'}
          </div>
          {isViewer ? (
            <span className={`pill ${cat.area === 'financial' ? 'pill-brand' : ''}`}>
              {cat.area === 'financial' ? 'Non-Operating' : 'Operating'}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => onChangeArea(cat.area === 'financial' ? 'operating' : 'financial')}
              className={`pill cursor-pointer ${cat.area === 'financial' ? 'pill-brand' : ''}`}
              title="Toggle between Operating and Non-Operating budget area"
            >
              {cat.area === 'financial' ? 'Non-Operating' : 'Operating'}
            </button>
          )}
        </div>

        {!collapsed && (
          <>
            {/* GL code search picker — registrar only */}
            {!isViewer && (
              <div className="mb-3 relative">
                <div className="flex items-center gap-1.5 border border-edge rounded-control px-2 py-1.5 bg-white focus-within:border-ccaanz-blue">
                  <Search className="w-3 h-3 text-ccaanz-grey flex-shrink-0" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setStagedIds(new Set()); // clear selection when query changes
                    }}
                    placeholder="Search GL code or name to add…"
                    className="flex-1 text-[12px] outline-none bg-transparent text-ccaanz-black placeholder:text-ccaanz-grey"
                  />
                </div>
                {searchResults.length > 0 && (
                  <div className="absolute left-0 right-0 top-full mt-1 border border-edge rounded-control bg-white shadow-sm z-10 overflow-hidden">
                    <div className="max-h-52 overflow-y-auto">
                      {searchResults.map(({ row, kind }) => {
                        const staged = stagedIds.has(row.id);
                        return (
                          <button
                            key={row.id}
                            type="button"
                            onClick={() => {
                              setStagedIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(row.id)) next.delete(row.id);
                                else next.add(row.id);
                                return next;
                              });
                            }}
                            className={`w-full text-left px-2 py-1.5 flex items-center gap-2 text-[12px] border-b border-paper-divider last:border-0 transition-colors ${
                              staged ? 'bg-ccaanz-blue/10' : 'hover:bg-paper-cream'
                            }`}
                          >
                            <span
                              className={`w-3.5 h-3.5 flex-shrink-0 rounded border flex items-center justify-center transition-colors ${
                                staged ? 'bg-ccaanz-blue border-ccaanz-blue' : 'border-edge'
                              }`}
                            >
                              {staged && (
                                <svg viewBox="0 0 10 8" className="w-2 h-2 text-white fill-current">
                                  <path
                                    d="M1 4l3 3 5-6"
                                    stroke="currentColor"
                                    strokeWidth="1.5"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                            <span
                              className={`text-[10px] font-medium uppercase tracking-wide w-8 flex-shrink-0 ${
                                kind === 'income' ? 'text-emerald-600' : 'text-ccaanz-red'
                              }`}
                            >
                              {kind === 'income' ? 'Inc' : 'Exp'}
                            </span>
                            <span className="font-mono w-12 text-ccaanz-grey flex-shrink-0">
                              {row.code || '—'}
                            </span>
                            <span className="flex-1 truncate text-ccaanz-black">{row.name}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="border-t border-edge px-2 py-1.5 flex items-center justify-between bg-paper-cream">
                      <span className="text-[11px] text-ccaanz-grey">
                        {stagedIds.size > 0 ? `${stagedIds.size} selected` : 'Click rows to select'}
                      </span>
                      <button
                        type="button"
                        disabled={stagedIds.size === 0}
                        onClick={() => {
                          const moves = searchResults
                            .filter(({ row }) => stagedIds.has(row.id))
                            .map(({ row, kind }) => ({ rowId: row.id, rowKind: kind }));
                          onBatchMoveRowsToSection(moves);
                          setStagedIds(new Set());
                          setSearchQuery('');
                        }}
                        className="btn btn-ghost btn-sm disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Add {stagedIds.size > 0 ? stagedIds.size : ''} GL code
                        {stagedIds.size !== 1 ? 's' : ''}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {items.length === 0 ? (
              <div className="text-[12px] italic text-ccaanz-grey px-1 py-2">
                {isViewer
                  ? 'No rows in this section.'
                  : 'Search for a GL code above, or drop a row from another section.'}
              </div>
            ) : (
              <ul className="space-y-0">
                {items
                  .slice()
                  .sort((a, b) => a.row.displayOrder - b.row.displayOrder)
                  .map(({ row, kind }) => (
                    <RowItem key={row.id} row={row} kind={kind} drag={drag} setDrag={setDrag} />
                  ))}
              </ul>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function RowItem({
  row,
  kind,
  drag,
  setDrag,
}: {
  row: LineItem;
  kind: Kind;
  drag: Drag;
  setDrag: (d: Drag) => void;
}) {
  const isViewer = useIsViewer();
  const isDragging = drag?.kind === 'row' && drag.rowId === row.id;
  return (
    <li
      className={`flex items-center gap-2 px-2 py-1.5 border-b border-paper-divider last:border-0 text-[13px] hover:bg-paper-cream transition-colors ${
        isDragging ? 'opacity-40' : ''
      }`}
      draggable={!isViewer}
      onDragStart={(e) => {
        if (isViewer) return;
        e.dataTransfer.effectAllowed = 'move';
        setDrag({ kind: 'row', rowId: row.id, rowKind: kind });
      }}
      onDragEnd={() => setDrag(null)}
    >
      {!isViewer && (
        <span
          className="cursor-grab text-ccaanz-grey hover:text-ccaanz-black flex-shrink-0"
          title="Drag to move row to another section"
          aria-label="Drag to move row to another section"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
      )}
      <span className="line-code num text-ccaanz-grey w-12 flex-shrink-0">{row.code || '—'}</span>
      <span className="line-name flex-1 text-ccaanz-black truncate">{row.name}</span>
    </li>
  );
}

// ─── Discrete Activities (projects) ─────────────────────────────────────

function DiscreteActivitiesPanel({
  projects,
  income,
  expenses,
  drag,
  hoverSectionId,
  setHoverSectionId,
  setDrag,
  onRename,
  onMoveByArrow,
  onMoveProjectToBefore,
  onAdd,
  onDelete,
  onMoveRowToProject,
}: {
  projects: Project[];
  income: LineItem[];
  expenses: LineItem[];
  drag: Drag;
  hoverSectionId: string | null;
  setHoverSectionId: (id: string | null) => void;
  setDrag: (d: Drag) => void;
  onRename: (id: string, name: string) => void;
  onMoveByArrow: (id: string, direction: -1 | 1) => void;
  onMoveProjectToBefore: (sourceId: string, targetId: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onMoveRowToProject: (rowId: string, kind: Kind, projectId: string) => void;
}) {
  const isViewer = useIsViewer();
  const sorted = [...projects].sort((a, b) => a.displayOrder - b.displayOrder);
  const rowsForProject = (projectId: string): { row: LineItem; kind: Kind }[] => [
    ...income
      .filter((r) => r.projectId === projectId && !r.deletedAt)
      .map((r) => ({ row: r, kind: 'income' as const })),
    ...expenses
      .filter((r) => r.projectId === projectId && !r.deletedAt)
      .map((r) => ({ row: r, kind: 'expense' as const })),
  ];

  return (
    <div>
      <div className="flex items-baseline justify-between flex-wrap gap-2 pb-2 border-b border-edge">
        <h2 className="text-[18px] font-semibold text-ccaanz-gold tracking-[0.005em]">
          Discrete Activities
        </h2>
        {!isViewer && (
          <button type="button" onClick={onAdd} className="btn btn-ghost btn-sm">
            <FolderPlus className="w-3.5 h-3.5" />
            New Activity
          </button>
        )}
      </div>
      {!isViewer && (
        <p className="text-[12px] text-ccaanz-grey mt-2 mb-4">
          Each activity holds a mix of income and expense rows. Drop a row from any income or
          expense section above to move it into an activity. Activities appear under "Discrete
          Activities" on the budget page with their own Net.
        </p>
      )}
      <div className="space-y-3">
        {sorted.length === 0 && (
          <div className="text-[13px] italic text-ccaanz-grey p-4 border border-dashed border-edge rounded-control">
            No activities yet. Click "New Activity" above.
          </div>
        )}
        {sorted.map((p, idx) => {
          const items = rowsForProject(p.id);
          return (
            <ProjectCard
              key={p.id}
              project={p}
              items={items}
              isFirst={idx === 0}
              isLast={idx === sorted.length - 1}
              drag={drag}
              isHover={hoverSectionId === p.id}
              setHoverSectionId={setHoverSectionId}
              setDrag={setDrag}
              onRename={(name) => onRename(p.id, name)}
              onMoveByArrow={(dir) => onMoveByArrow(p.id, dir)}
              onMoveProjectToBefore={onMoveProjectToBefore}
              onDelete={() => onDelete(p.id)}
              onMoveRowToProject={onMoveRowToProject}
            />
          );
        })}
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  items,
  isFirst,
  isLast,
  drag,
  isHover,
  setHoverSectionId,
  setDrag,
  onRename,
  onMoveByArrow,
  onMoveProjectToBefore,
  onDelete,
  onMoveRowToProject,
}: {
  project: Project;
  items: { row: LineItem; kind: Kind }[];
  isFirst: boolean;
  isLast: boolean;
  drag: Drag;
  isHover: boolean;
  setHoverSectionId: (id: string | null) => void;
  setDrag: (d: Drag) => void;
  onRename: (name: string) => void;
  onMoveByArrow: (direction: -1 | 1) => void;
  onMoveProjectToBefore: (sourceId: string, targetId: string) => void;
  onDelete: () => void;
  onMoveRowToProject: (rowId: string, kind: Kind, projectId: string) => void;
}) {
  const isViewer = useIsViewer();
  const dragAcceptable =
    !isViewer &&
    drag &&
    (drag.kind === 'row'
      ? true // projects accept any kind
      : drag.kind === 'project' && drag.projectId !== project.id);

  const onDragOver = (e: React.DragEvent<HTMLElement>) => {
    if (!dragAcceptable) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!isHover) setHoverSectionId(project.id);
  };
  const onDragLeave = (e: React.DragEvent<HTMLElement>) => {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    if (isHover) setHoverSectionId(null);
  };
  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    if (!drag) return;
    e.preventDefault();
    if (drag.kind === 'row') {
      onMoveRowToProject(drag.rowId, drag.rowKind, project.id);
    } else if (drag.kind === 'project') {
      onMoveProjectToBefore(drag.projectId, project.id);
    }
    setDrag(null);
    setHoverSectionId(null);
  };

  // Sort items by displayOrder; income/expense indicator stays with the row.
  const sortedItems = [...items].sort((a, b) => a.row.displayOrder - b.row.displayOrder);

  return (
    <section
      className={`panel transition-shadow ${
        isHover && dragAcceptable ? 'ring-2 ring-ccaanz-gold ring-offset-1' : ''
      }`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="panel-section">
        <div className="flex items-center gap-2 mb-3">
          {!isViewer && (
            <span
              className="cursor-grab text-ccaanz-grey hover:text-ccaanz-black"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                setDrag({ kind: 'project', projectId: project.id });
              }}
              onDragEnd={() => {
                setDrag(null);
                setHoverSectionId(null);
              }}
              title="Drag to reorder activity"
              aria-label="Drag to reorder activity"
            >
              <GripVertical className="w-4 h-4" />
            </span>
          )}
          <input
            type="text"
            value={project.name}
            readOnly={isViewer}
            onChange={(e) => onRename(e.target.value)}
            className="input flex-1 text-[14px] font-semibold text-ccaanz-blue"
          />
          {!isViewer && (
            <>
              <button
                type="button"
                onClick={() => onMoveByArrow(-1)}
                disabled={isFirst}
                className="row-control"
                title="Move activity up"
                aria-label="Move activity up"
              >
                <ArrowUp className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => onMoveByArrow(1)}
                disabled={isLast}
                className="row-control"
                title="Move activity down"
                aria-label="Move activity down"
              >
                <ArrowDown className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={onDelete}
                className="row-control danger"
                title="Delete activity"
                aria-label="Delete activity"
              >
                <FolderX className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>

        <div className="text-[11px] uppercase tracking-[0.08em] text-ccaanz-grey mb-2">
          {sortedItems.length} row{sortedItems.length === 1 ? '' : 's'}
        </div>

        {sortedItems.length === 0 ? (
          <div className="text-[12px] italic text-ccaanz-grey px-1 py-2">
            {isViewer
              ? 'No rows in this activity.'
              : 'Drop a row from any operating section above.'}
          </div>
        ) : (
          <ul className="space-y-0">
            {sortedItems.map(({ row, kind }) => (
              <ProjectRow key={row.id} row={row} kind={kind} drag={drag} setDrag={setDrag} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ProjectRow({
  row,
  kind,
  drag,
  setDrag,
}: {
  row: LineItem;
  kind: Kind;
  drag: Drag;
  setDrag: (d: Drag) => void;
}) {
  const isViewer = useIsViewer();
  const isDragging = drag?.kind === 'row' && drag.rowId === row.id;
  return (
    <li
      className={`flex items-center gap-2 px-2 py-1.5 border-b border-paper-divider last:border-0 text-[13px] hover:bg-paper-cream transition-colors ${
        isDragging ? 'opacity-40' : ''
      }`}
      draggable={!isViewer}
      onDragStart={(e) => {
        if (isViewer) return;
        e.dataTransfer.effectAllowed = 'move';
        setDrag({ kind: 'row', rowId: row.id, rowKind: kind });
      }}
      onDragEnd={() => setDrag(null)}
    >
      {!isViewer && (
        <span
          className="cursor-grab text-ccaanz-grey hover:text-ccaanz-black flex-shrink-0"
          title="Drag to move row out of this activity"
          aria-label="Drag to move row out of this activity"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </span>
      )}
      <span
        className={`text-[10px] uppercase tracking-[0.08em] font-medium w-20 flex-shrink-0 ${
          kind === 'income' ? 'text-emerald-600' : 'text-ccaanz-red'
        }`}
        title={kind === 'income' ? 'Income line' : 'Expense line'}
      >
        {kind === 'income' ? 'Income' : 'Expense'}
      </span>
      <span className="line-code num text-ccaanz-grey w-14 flex-shrink-0">{row.code || '—'}</span>
      <span className="line-name flex-1 text-ccaanz-black truncate">{row.name}</span>
    </li>
  );
}
